import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, ifMatch, newIdempotencyKey, unwrap } from '@/api/client';
import type { components } from '@/api/generated/schema';
import { useCommand } from '@/app/commands';
import { useOrgId } from '@/app/session';
import { Button, Select, TextInput, Time, Unknown, domainOptions, statusDef } from '@/components/ui';
import { formatNumber, humanize } from '@/lib/format';
import { cx } from '@/lib/cx';
import { fieldDefinitionsQuery, formatMw, siteChangedKeys, type FieldDefinition, type Location, type LocationChanges } from './api';

type S = components['schemas'];
type PowerField = 'available_capacity_kw' | 'existing_capacity_kw' | 'planned_capacity_kw' | 'voltage_kv';
type EditSpec =
  | { kind: 'power'; field: PowerField; unit: 'MW' | 'kV' }
  | { kind: 'grid' }
  | { kind: 'fact'; key: string; valueType: S['FieldValueType']; unitLabel: string | null };

interface FactRow {
  id: string;
  label: string;
  display: ReactNode;
  /** Text the editor starts with. */
  initial?: string;
  edit?: EditSpec;
}

/** The big figures: what a site owner conversation is usually about. */
const FIGURE_FACTS = ['mw_expandable', 'building_m2'];
/** First in the compact list. */
const NEXT_FACTS = ['asking_price', 'land_ha', 'fibre_carriers'];
/** Compact rows shown before "Show all". */
const COMPACT_SHOWN = 6;
const DECIMAL = /^\d+(\.\d+)?$/;

const unitLabel = (u: string | null) => (u === 'm2' ? 'm²' : u);
const figureFormat = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 });

/** "12.5" MW to "12500" kW without floating point drift. */
function mwToKw(mw: string): string {
  const [int = '0', frac = ''] = mw.split('.');
  const kw = `${int}${frac.padEnd(3, '0').slice(0, 3)}${frac.length > 3 ? `.${frac.slice(3)}` : ''}`;
  return kw.replace(/^0+(?=\d)/, '');
}

function kwToMw(kw: string | null): string {
  if (kw == null) return '';
  const n = Number(kw) / 1000;
  return Number.isFinite(n) ? String(Number(n.toFixed(6))) : '';
}

const trimDecimal = (d: string | null | undefined) => (d == null ? '' : String(Number(d)));

/** A big number with its unit set smaller: "12 MW". */
function Figure({ value, unit }: { value: number; unit: string | null }) {
  return (
    <span className="fig-text">
      <span className="fig-num num">{figureFormat.format(value)}</span>
      {unit ? <span className="fig-unit">{unit}</span> : null}
    </span>
  );
}

function factDisplay(v: S['FactValue'] | undefined, def: FieldDefinition | undefined, big: boolean): ReactNode {
  if (!v || v.kind === 'null') return <Unknown />;
  const unit = unitLabel(def?.unit ?? null);
  switch (v.kind) {
    case 'decimal':
      if (big && Number.isFinite(Number(v.decimal))) return <Figure value={Number(v.decimal)} unit={unit} />;
      return <span className="num">{def?.key === 'year_built' ? trimDecimal(v.decimal) : formatNumber(v.decimal, unit ?? undefined)}</span>;
    case 'text':
      return v.text ? v.text : <Unknown />;
    case 'boolean':
      return v.boolean === undefined ? <Unknown /> : v.boolean ? 'Yes' : 'No';
    case 'date':
      return v.date ?? <Unknown />;
  }
}

function factInitial(v: S['FactValue'] | undefined): string {
  if (!v) return '';
  if (v.kind === 'decimal') return trimDecimal(v.decimal);
  if (v.kind === 'text') return v.text ?? '';
  return '';
}

function buildRows(l: Location, defs: Map<string, FieldDefinition> | undefined): { figures: FactRow[]; rest: FactRow[] } {
  const facts = new Map(l.facts.map((f) => [f.field_key, f]));
  const factRow = (key: string, big = false): FactRow | null => {
    const def = defs?.get(key);
    const fact = facts.get(key);
    if (!def && !fact && !big) return null;
    const valueType = def?.value_type ?? (fact?.value.kind === 'decimal' || big ? 'number' : 'text');
    const editable = valueType === 'number' || valueType === 'text' || valueType === 'url';
    return {
      id: `fact-${key}`,
      label: def?.label ?? humanize(key),
      display: factDisplay(fact?.value, def, big),
      initial: factInitial(fact?.value),
      edit: editable ? { kind: 'fact', key, valueType, unitLabel: valueType === 'number' ? unitLabel(def?.unit ?? null) : null } : undefined,
    };
  };
  const power = (field: PowerField, label: string, big = false): FactRow => {
    const raw = l.power[field];
    const isKv = field === 'voltage_kv';
    const n = raw == null ? NaN : Number(raw);
    return {
      id: field,
      label,
      display:
        raw == null || !Number.isFinite(n) ? (
          <Unknown />
        ) : big ? (
          <Figure value={isKv ? n : n / 1000} unit={isKv ? 'kV' : 'MW'} />
        ) : (
          <span className="num">{isKv ? formatNumber(raw, 'kV') : formatMw(raw)}</span>
        ),
      initial: isKv ? trimDecimal(raw) : kwToMw(raw),
      edit: { kind: 'power', field, unit: isKv ? 'kV' : 'MW' },
    };
  };
  const grid = l.power.grid_connection_status;
  const figures: FactRow[] = [
    power('available_capacity_kw', 'Available', true),
    { ...factRow('mw_expandable', true)!, label: 'Expandable' },
    { ...factRow('building_m2', true)!, label: 'Building' },
    {
      id: 'grid',
      label: 'Grid',
      display: grid === 'unknown' ? <Unknown /> : <span className="fig-word">{statusDef('grid', grid).label}</span>,
      initial: grid,
      edit: { kind: 'grid' },
    },
  ];
  const first = new Set([...FIGURE_FACTS, ...NEXT_FACTS]);
  const otherFactKeys = [...new Set([...(defs?.keys() ?? []), ...facts.keys()])].filter((k) => !first.has(k)).sort();
  const rest: FactRow[] = [
    ...NEXT_FACTS.map((k) => factRow(k)).filter((r): r is FactRow => r !== null),
    power('voltage_kv', 'Voltage'),
    power('existing_capacity_kw', 'Existing power'),
    power('planned_capacity_kw', 'Planned power'),
    ...otherFactKeys.map((k) => factRow(k)).filter((r): r is FactRow => r !== null),
    {
      id: 'expansion',
      label: 'Earliest expansion',
      display: l.power.earliest_expansion_date ?? <Unknown />,
    },
    {
      id: 'networks',
      label: 'Networks',
      display: l.networks.length ? (
        l.networks.map((n) => [humanize(n.connectivity_type), n.capacity_mbps ? formatNumber(n.capacity_mbps, 'Mbps') : null].filter(Boolean).join(' ')).join(', ')
      ) : (
        <Unknown />
      ),
    },
    {
      id: 'approvals',
      label: 'Permits',
      display: l.municipal_approvals.length ? (
        l.municipal_approvals.map((a) => `${a.approval_kind} (${humanize(a.status)})`).join(', ')
      ) : (
        <Unknown>No permits</Unknown>
      ),
    },
    { id: 'added', label: 'Added', display: <Time value={l.ingested_at} /> },
  ];
  return { figures, rest };
}

/** Big figures (power, expansion, building, grid), then the rest as compact rows. Values edit in place. */
export function KeyFacts({ location: l, columns = 2 }: { location: Location; columns?: 1 | 2 }) {
  const orgId = useOrgId();
  const defs = useQuery(fieldDefinitionsQuery(orgId));
  const [all, setAll] = useState(false);
  const { figures, rest } = buildRows(l, defs.data);
  const readOnly = Boolean(l.deleted_at);
  const cmd = useCommand({
    run: ({ changes, label }: { changes: LocationChanges; label: string }) =>
      unwrap(
        api.PATCH('/api/v1/orgs/{orgId}/locations/{locationId}', {
          params: { path: { orgId, locationId: l.id }, header: { 'If-Match': ifMatch(l.version), 'Idempotency-Key': newIdempotencyKey() } },
          body: { changes, reason: `Edited ${label.toLowerCase()}` },
        }),
      ),
    success: 'Fact saved',
    invalidate: siteChangedKeys(orgId, l.id),
  });

  const save = async (row: FactRow, text: string): Promise<string | null> => {
    const spec = row.edit;
    if (!spec) return null;
    const v = text.trim();
    let changes: LocationChanges;
    if (spec.kind === 'grid') {
      changes = { power: { ...l.power, grid_connection_status: v as S['GridConnectionStatus'] } };
    } else if (spec.kind === 'power') {
      if (v && !DECIMAL.test(v)) return 'Numbers only';
      changes = { power: { ...l.power, [spec.field]: v ? (spec.unit === 'MW' ? mwToKw(v) : v) : null } };
    } else {
      if (spec.valueType === 'number' && v && !DECIMAL.test(v.replace(/,/g, ''))) return 'Numbers only';
      const value: S['FactValue'] = !v ? { kind: 'null' } : spec.valueType === 'number' ? { kind: 'decimal', decimal: v.replace(/,/g, '') } : { kind: 'text', text: v };
      changes = { facts: [{ field_key: spec.key, value }] };
    }
    try {
      await cmd.mutateAsync({ changes, label: row.label });
      return null;
    } catch {
      return 'Not saved';
    }
  };

  const value = (row: FactRow, big: boolean) =>
    row.edit && !readOnly ? <EditableValue row={row} big={big} onSave={(t) => save(row, t)} /> : <span className="fact-static">{row.display}</span>;
  const shown = all ? rest : rest.slice(0, COMPACT_SHOWN);

  return (
    <div className="facts">
      <dl className="figures">
        {figures.map((row) => (
          <div key={row.id} className="figure">
            <dt>{row.label}</dt>
            <dd>{value(row, true)}</dd>
          </div>
        ))}
      </dl>
      <dl className={cx('facts-compact', columns === 1 && 'facts-compact-1')}>
        {shown.map((row) => (
          <div key={row.id} className="fact">
            <dt>{row.label}</dt>
            <dd>{value(row, false)}</dd>
          </div>
        ))}
      </dl>
      {rest.length > COMPACT_SHOWN ? (
        <Button size="sm" variant="quiet" icon={all ? 'chevronDown' : 'chevronRight'} aria-expanded={all} onClick={() => setAll((a) => !a)} className="facts-more">
          {all ? 'Fewer facts' : `More facts ${rest.length - COMPACT_SHOWN}`}
        </Button>
      ) : null}
    </div>
  );
}

function EditableValue({ row, big, onSave }: { row: FactRow; big: boolean; onSave: (text: string) => Promise<string | null> }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const spec = row.edit;
  const start = () => {
    setText(row.initial ?? '');
    setError(null);
    setEditing(true);
  };
  // Enter submits, then the blur that follows must not save a second time.
  const saving = useRef(false);
  const commit = async (value: string) => {
    if (saving.current) return;
    if (value.trim() === (row.initial ?? '')) {
      setEditing(false);
      return;
    }
    saving.current = true;
    setBusy(true);
    const err = await onSave(value);
    saving.current = false;
    setBusy(false);
    if (err) setError(err);
    else setEditing(false);
  };
  if (!editing || !spec) {
    return (
      <button type="button" className={cx('fact-value', big && 'fact-value-big')} onClick={start} title={`Edit ${row.label}`}>
        {row.display}
        <span className="visually-hidden">. Edit {row.label}</span>
      </button>
    );
  }
  if (spec.kind === 'grid') {
    return (
      <Select
        autoFocus
        aria-label={row.label}
        className="fact-input"
        disabled={busy}
        value={text}
        options={domainOptions('grid')}
        onChange={(e) => void commit(e.target.value)}
        onBlur={() => !busy && setEditing(false)}
        onKeyDown={(e) => e.key === 'Escape' && (e.stopPropagation(), setEditing(false))}
      />
    );
  }
  const unit = spec.kind === 'power' ? spec.unit : spec.unitLabel;
  return (
    <form
      className="fact-edit"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        void commit(text);
      }}
    >
      <TextInput
        autoFocus
        aria-label={row.label}
        aria-invalid={error ? true : undefined}
        className="fact-input"
        inputMode={spec.kind === 'power' || (spec.kind === 'fact' && spec.valueType === 'number') ? 'decimal' : undefined}
        value={text}
        readOnly={busy}
        placeholder="Unknown"
        onChange={(e) => setText(e.target.value)}
        onBlur={() => void commit(text)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            setEditing(false);
          }
        }}
      />
      {unit ? <span className="faint">{unit}</span> : null}
      {error ? <span className="field-error">{error}</span> : null}
    </form>
  );
}
